import { FACTURACION_SCALE, ANTIGUEDAD_SCALE, EMPLEADOS_SCALE } from '../config/configListing';
import { empleadosLowerBoundForCount, empleadosIdxForLowerBound } from '../config/configFilters';
import { MANDATO_RANGE_GROUPS } from './mandatoRanges';

const targetNames = {
  facturacion: 'targetTurnover',
  ebitda: 'targetEbitda',
  empleados: 'targetEmployees',
  antiguedad: 'targetAge',
};

/** Use the publication contract for every search interval, including “Cualquiera”. */
export const VALORADOR_RANGES = MANDATO_RANGE_GROUPS.map(group => ({
  ...group,
  name: targetNames[group.id],
}));

const numberOrNull = value =>
  value == null || value === '' || !Number.isFinite(Number(value)) ? null : Number(value);
const copySelection = value => (Array.isArray(value) ? [...new Set(value)] : []);

/**
 * Build only allowed public business fields. Exact finances never enter this object.
 * Account profile, generated title and transaction configuration are added by publication.
 * Missing inputs remain invalid: they must never become zero or “Cualquiera”.
 */
export const valoradorPublicData = (answers, currentYear = new Date().getFullYear()) => {
  if (answers.mode === 'buyer') {
    return {
      listingType: 'mandato-compra',
      sector: answers.targetSector,
      provincias: copySelection(answers.targetProvinces),
      modalidades: copySelection(answers.targetModalities),
      tiposOperacion: copySelection(answers.targetOperations),
      ...VALORADOR_RANGES.reduce(
        (result, { name, minKey, maxKey }) => ({
          ...result,
          [minKey]: numberOrNull(answers[`${name}Min`]),
          [maxKey]: numberOrNull(answers[`${name}Max`]),
        }),
        {}
      ),
    };
  }
  if (answers.mode !== 'seller') return {};

  const turnover = numberOrNull(answers.turnover);
  const employees = numberOrNull(answers.employees);
  const year = numberOrNull(answers.year);
  const revenueBand =
    answers.facturacion !== undefined
      ? FACTURACION_SCALE.find(row => row.option === answers.facturacion)
      : turnover != null && turnover >= 0
      ? FACTURACION_SCALE.find(row => row.upperBound == null || turnover / 1000 < row.upperBound)
      : null;
  const ageBand =
    answers.antiguedad !== undefined
      ? ANTIGUEDAD_SCALE.find(row => row.option === answers.antiguedad)
      : Number.isInteger(year) && year >= 1900 && year <= currentYear
      ? ANTIGUEDAD_SCALE.find(row => row.upperBound == null || currentYear - year < row.upperBound)
      : null;
  const team =
    answers.empleados !== undefined
      ? EMPLEADOS_SCALE.find(row => row.lowerBound === numberOrNull(answers.empleados))
          ?.lowerBound ?? null
      : Number.isInteger(employees) && employees >= 1
      ? empleadosLowerBoundForCount(employees)
      : null;
  return {
    listingType: 'free-listing',
    sector: answers.sector,
    provincia: answers.province,
    modalidad: answers.modality,
    tipo_operacion: copySelection(answers.operations),
    facturacion: revenueBand?.option ?? null,
    facturacionIdx: revenueBand?.idx ?? null,
    empleados: team,
    empleadosIdx: empleadosIdxForLowerBound(team),
    antiguedad: ageBand?.option ?? null,
    antiguedadIdx: ageBand?.idx ?? null,
  };
};

/** Upgrade the old local prototype without inventing exact figures from bands. */
export const migrateValoradorAnswers = (answers, currentYear = new Date().getFullYear()) => {
  const data = valoradorPublicData({ ...answers, mode: 'seller' }, currentYear);
  const { turnover, employees, year, ...rest } = answers;
  return {
    ...rest,
    facturacion: data.facturacion,
    empleados: data.empleados == null ? undefined : String(data.empleados),
    antiguedad: data.antiguedad,
  };
};

/**
 * Approximate inputs for Alfredo's engine; never public listing data.
 * Open-ended bands use their displayed threshold +25%, as agreed with the user.
 * The original engine expects a calendar year and a team category, not a raw age/count.
 */
export const valoradorBandEstimates = (answers, currentYear = new Date().getFullYear()) => {
  const revenue = FACTURACION_SCALE.find(row => row.option === answers.facturacion);
  const teamIndex = EMPLEADOS_SCALE.findIndex(
    row => String(row.lowerBound) === String(answers.empleados)
  );
  const team = EMPLEADOS_SCALE[teamIndex];
  const age = ANTIGUEDAD_SCALE.find(row => row.option === answers.antiguedad);
  const midpoint = row =>
    row == null
      ? null
      : row.upperBound == null
      ? row.lowerBound * 1.25
      : (row.lowerBound + row.upperBound) / 2;
  // “+100” is open despite its legacy technical upperBound=200.
  const employees = !team
    ? null
    : teamIndex === EMPLEADOS_SCALE.length - 1
    ? EMPLEADOS_SCALE[teamIndex - 1].upperBound * 1.25
    : midpoint(team);
  const years = midpoint(age);
  return {
    facturacion: revenue ? midpoint(revenue) * 1000 : null,
    empleados: employees,
    antiguedad: years,
    anio: years == null ? null : currentYear - Math.round(years),
    emp:
      employees == null
        ? null
        : employees <= 1
        ? 'e0'
        : employees <= 3
        ? 'e1'
        : employees <= 10
        ? 'e2'
        : employees <= 25
        ? 'e3'
        : employees <= 50
        ? 'e4'
        : 'e5',
  };
};

/** Rebuild canonical public values and indices after editing the prepared advertisement. */
export const editedValoradorPublicData = data =>
  data.listingType === 'mandato-compra'
    ? valoradorPublicData({
        mode: 'buyer',
        targetSector: data.sector,
        targetProvinces: data.provincias,
        targetModalities: data.modalidades,
        targetOperations: data.tiposOperacion,
        ...Object.fromEntries(
          VALORADOR_RANGES.flatMap(r => [
            [`${r.name}Min`, data[r.minKey]],
            [`${r.name}Max`, data[r.maxKey]],
          ])
        ),
      })
    : valoradorPublicData({
        mode: 'seller',
        sector: data.sector,
        province: data.provincia,
        modality: data.modalidad,
        operations: data.tipo_operacion,
        facturacion: data.facturacion,
        empleados: data.empleados,
        antiguedad: data.antiguedad,
      });
